import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Grid,
  Hidden,
  LinearProgress,
  Stack,
  Tooltip,
  Button,
  Breadcrumbs,
  Link,
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { useHistory } from 'react-router';
import { Formik } from 'formik';
import { MFSubmitButton, MFTextField } from '../../lib/formik';
import { DatePicker } from '../../lib/formik/DatePicker';
import { useDispatch } from 'react-redux';
import MFSelectField from '../../lib/formik/SelectField';
import { getFundManagersList, getTrustessList } from '../../redux-store/actions/userManagement';
import { DistributorListType, FundManager, Trustee } from '../../redux-store/types/api-types';
import {
  fundAccess,
  getFundDraft,
  getPlanList,
  saveFundDraftStep,
  submitFundDraft,
  updateFund,
} from '../../redux-store/actions/funds';
import { Location } from 'history';
import { accessType, addFundPlanType, addSetupFee, FundProps } from '../../redux-store/types/funds';
import {
  addFundStep1Validation,
  addFundStep2Validation,
  addFundStep3Validation,
  addFundValidation,
  editFundValidation,
} from '../../utils/schema';
import {
  allowOnlyNumbers,
  currencyConversion,
  isApplicableSetupfee,
  isFieldApplicable,
  getArrayLength,
  isFieldVariable,
} from '../../utils/utilityFunctions';
import {
  CurrencyMaster,
  FEE_TYPE_MASTER,
  YesNoBooleanMaster,
  YesNoMaster,
  defaultCurrency,
} from '../../utils/constant';
import { getDistributorsListForFilters } from '../../redux-store/actions';
import { MultipleSelect } from '../../lib/formik/MultipleSelectField';
import MFCheckbox from '../../lib/formik/Checkbox';
import UseRadioGroup from '../../lib/formik/Radio';
import { newSetupFee, updatedSetupFee } from './addPlan';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ConfirmationDialog } from '../commonComponents';
import AddIcon from '@mui/icons-material/Add';
import { DataTable } from '../DataTable';
import { IS_STAMP_PAPER_SERIES_MASTER } from '../../utils/constant';
import { RequiredFieldsMessage } from '../RequiredFieldsMessage';
import { Link as RouterLink } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import {
  buildStepData,
  extractFundDraft,
  mapDraftToForm,
  sectionToStep,
  validateYupSchemaOnValues,
} from './fundDraftUtils';

type FundFormSection = 'basic' | 'plan' | 'investment';

const FUND_SECTIONS: {
  key: FundFormSection;
  stepperLabel: string;
  title: string;
  subtitle: string;
}[] = [
  {
    key: 'basic',
    stepperLabel: 'Basic Information',
    title: 'Basic Information',
    subtitle: 'Define scheme and bank information',
  },
  {
    key: 'plan',
    stepperLabel: 'Plan & Structure',
    title: 'Plan Details',
    subtitle: 'Define plan level information',
  },
  {
    key: 'investment',
    stepperLabel: 'Codes & Identifiers',
    title: 'Investment & Commitments',
    subtitle: 'Define investment and commitment settings',
  },
];

function FundFormStepper({
  activeKey,
  onSelect,
}: {
  activeKey: FundFormSection;
  onSelect: (key: FundFormSection) => void;
}): JSX.Element {
  const activeIndex = FUND_SECTIONS.findIndex((s) => s.key === activeKey);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        width: '100%',
        maxWidth: 760,
        mx: 'auto',
        mb: { xs: 3, md: 4 },
        px: { xs: 1, sm: 2 },
      }}>
      {FUND_SECTIONS.map((section, index) => {
        const isActive = section.key === activeKey;
        const isCompleted = index < activeIndex;
        return (
          <Box
            key={section.key}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              flex: index < FUND_SECTIONS.length - 1 ? 1 : '0 0 auto',
              minWidth: 0,
            }}>
            <Box
              component="button"
              type="button"
              onClick={() => onSelect(section.key)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                border: 'none',
                bgcolor: 'transparent',
                cursor: 'pointer',
                p: 0,
                minWidth: { xs: 88, sm: 130 },
              }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: isCompleted ? '#12B76A' : isActive ? '#2057A6' : '#FFFFFF',
                  border: isCompleted || isActive ? 'none' : '2px solid #D0D5DD',
                  color: isCompleted || isActive ? '#FFFFFF' : '#98A2B3',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: 14,
                  mb: 1,
                  flexShrink: 0,
                }}>
                {isCompleted ? <CheckIcon sx={{ fontSize: 18 }} /> : index + 1}
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: { xs: 12, sm: 14 },
                  fontWeight: isActive ? 600 : 500,
                  color: isCompleted ? '#12B76A' : isActive ? '#2057A6' : '#98A2B3',
                  textAlign: 'center',
                  lineHeight: 1.3,
                }}>
                {section.stepperLabel}
              </Typography>
            </Box>
            {index < FUND_SECTIONS.length - 1 && (
              <Box
                sx={{
                  flex: 1,
                  height: 0,
                  borderTop: '2px dashed',
                  borderColor: index < activeIndex ? '#12B76A' : '#D0D5DD',
                  mt: '15px',
                  mx: { xs: 0.5, sm: 1.5 },
                  minWidth: 16,
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function FundSectionHeading({ title, subtitle }: { title: string; subtitle: string }): JSX.Element {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 18,
          fontWeight: 700,
          color: '#2057A6',
          mb: 0.5,
        }}>
        {title}
      </Typography>
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 13,
          fontWeight: 400,
          color: '#6B7280',
        }}>
        {subtitle}
      </Typography>
    </Box>
  );
}

type NewPlanType = addFundPlanType & {
  isApplicableForDistributor: boolean;
};
const initialValues: NewPlanType = {
  schemeCode: '',
  schemeName: '',
  amcCode: '',
  bankName: '',
  bankBranch: '',
  ifscCode: '',
  bankAccountNumber: '',
  id: '',
  schemeId: null,
  planCode: '',
  planDescription: '',
  schemeNature: '',
  registrationNumber: '',
  schemeCategory: '',
  country: '',
  currency: defaultCurrency,
  schemeStartDate: `${new Date()}`,
  lockInPeriod: '',
  lockInExtension: '',
  inActive: true,
  faceValue: 0,
  fundAddress1: '',
  fundAddress2: '',
  fundAddress3: '',
  isActive: true,
  custodian: '',
  setupFee: 0,
  fundmanagerIds: null,
  trusteeIds: null,
  requiredFundManagers: null,
  requiredTrustees: null,
  minCommitmentAmount: null,
  maxCommitmentAmount: 0,
  minContributionPercentage: 0,
  managementFee: 0,
  placementFeeApplicable: '',
  placementFee: null,
  operatingExpensesApplicable: '',
  operatingExpenses: null,
  offeringExpenses: 0,
  tcLink: '',
  distributorIds: null,
  isApplicableForDirect: false,
  isApplicableForDistributor: false,
  setupFeeValues: [newSetupFee],
  setupFeeApplicable: 'yes',
  equalisationPercentValues: [newSetupFee],
  equalisationPercentApplicable: 'yes',
  netCommitmentAmtApplicable: '',
  equalisationAmtApplicable: '',
  equalisationAmtType: '',
  minEqualisationAmt: null,
  maxEqualisationAmt: null,
  defaultEqualisationAmt: null,
  CVL_PWD: '',
  CVL_PASSCODE: '',
  CVL_USERNAME: '',
  CVL_APP_POS_CODE: '',
  CVL_ENABLE_NEW_API: null,
  CVL_JSON_AES_KEY: '',
  CVL_JSON_API_KEY: '',
  LEEGALITY_STAMP_SERIES: '',
  LEEGALITY_STAMP_SERIES_GROUP: '',
  LEEGALITY_STAMP_VALUE: null,
  LEEGALITY_USE_STAMP_SERIES: null,
  indMinNetworth: null,
  nonIndMinNetworth: null,
};

export default function AddFund({
  location,
}: {
  location: Location<{ fundId?: number; draftId?: string }>;
}): JSX.Element {
  const history = useHistory();
  const dispatch = useDispatch();
  const [fundManager, setFundManager] = useState<any[]>([]);
  const [editField, setEditField] = useState(initialValues);
  const [loading, setLoading] = useState(false);
  const { fundId, draftId: locationDraftId } = location.state || {};
  const [savedDraftId, setSavedDraftId] = useState<string | undefined>(
    locationDraftId ? String(locationDraftId) : undefined
  );
  const draftIdRef = useRef<string | undefined>(
    locationDraftId ? String(locationDraftId) : undefined
  );
  const [access, setAccess] = useState(false);
  const [trustee, setTrustee] = useState<any[]>([]);
  const [distributorList, setDistributorList] = useState<DistributorListType[]>([]);
  const [plansCount, setPlansCount] = useState(0);
  const [activeSection, setActiveSection] = useState<FundFormSection>('basic');
  const [setupFeeToDelete, setSetupFeeToDelete] = useState<{
    index: number;
  } | null>(null);
  const [_equalisationPercentageToDelete, setEqualisationPercentageToDelete] = useState<{
    index: number;
  } | null>(null);

  useEffect(() => {
    let isComponentAlive = true;
    (async function () {
      try {
        setLoading(true);
        const trusteeList = (await dispatch(
          getTrustessList({ isActive: true })
        )) as unknown as Trustee[];
        const fundManagerList = (await dispatch(
          getFundManagersList({ isActive: true })
        )) as unknown as FundManager[];
        const { fundSetupAccess } = (await dispatch(fundAccess())) as unknown as accessType;
        setAccess(fundSetupAccess);
        setFundManager(fundManagerList);
        setTrustee(trusteeList);
        const res = (await dispatch(
          getDistributorsListForFilters({ isActive: true })
        )) as unknown as DistributorListType[];
        setDistributorList(res);

        if (fundId) {
          const GetFundDetail = (await dispatch(
            getPlanList(fundId.toString())
          )) as unknown as FundProps;
          const {
            schemeCode,
            schemeName,
            amcCode,
            bankName,
            bankBranch,
            ifscCode,
            bankAccountNumber,
            CVL_PWD,
            CVL_PASSCODE,
            CVL_USERNAME,
            CVL_APP_POS_CODE,
            CVL_ENABLE_NEW_API = null,
            CVL_JSON_AES_KEY = '',
            CVL_JSON_API_KEY = '',
            LEEGALITY_STAMP_SERIES = '',
            LEEGALITY_STAMP_SERIES_GROUP = '',
            LEEGALITY_STAMP_VALUE = null,
            LEEGALITY_USE_STAMP_SERIES = null,
            indMinNetworth = null,
            nonIndMinNetworth = null,
          } = GetFundDetail || {};

          setPlansCount(GetFundDetail?.plans?.length || 0);
          setEditField({
            ...editField,
            schemeCode,
            schemeName,
            amcCode,
            bankName,
            bankBranch,
            ifscCode,
            bankAccountNumber,
            CVL_PWD,
            CVL_PASSCODE,
            CVL_USERNAME,
            CVL_APP_POS_CODE,
            CVL_ENABLE_NEW_API,
            CVL_JSON_AES_KEY,
            CVL_JSON_API_KEY,
            LEEGALITY_STAMP_SERIES,
            LEEGALITY_STAMP_SERIES_GROUP,
            LEEGALITY_STAMP_VALUE,
            LEEGALITY_USE_STAMP_SERIES: LEEGALITY_USE_STAMP_SERIES || false,
            indMinNetworth,
            nonIndMinNetworth,
          });
        } else if (locationDraftId && /^\d+$/.test(String(locationDraftId))) {
          const draftRes = await dispatch(getFundDraft(String(locationDraftId)));
          const draft = extractFundDraft(draftRes);
          if (draft) {
            setSavedDraftId(String(draft.id));
            draftIdRef.current = String(draft.id);
            setEditField(mapDraftToForm(draft, initialValues));
            const step = Number(draft.currentStep) || 1;
            setActiveSection(step === 2 ? 'plan' : step >= 3 ? 'investment' : 'basic');
          }
        }
        if (!isComponentAlive) return;
        setLoading(false);
      } catch (e) {
        console.error((e as Error).message);
      } finally {
        if (isComponentAlive) {
          setLoading(false);
        }
      }
    })();
    return () => {
      isComponentAlive = false;
    };
  }, [locationDraftId, fundId]);

  const persistDraftStep = async (
    step: 1 | 2 | 3,
    values: NewPlanType,
    draftIdOverride?: string,
    showToast = false
  ): Promise<string | undefined> => {
    const currentId = draftIdOverride || draftIdRef.current || savedDraftId;
    const body = {
      ...(currentId ? { draftId: currentId } : {}),
      currentStep: step,
      data: buildStepData(step, values),
    };
    const res = await dispatch(saveFundDraftStep(body, { showToast }));
    const draft = extractFundDraft(res);
    const nextId = draft?.id ? String(draft.id) : currentId;
    if (nextId) {
      draftIdRef.current = nextId;
      if (nextId !== savedDraftId) {
        setSavedDraftId(nextId);
      }
    }
    return nextId;
  };

  const completeDraftFund = async (values: NewPlanType) => {
    let draftId = draftIdRef.current || savedDraftId;
    draftId = (await persistDraftStep(1, values, draftId)) || draftId;
    draftId = (await persistDraftStep(2, values, draftId)) || draftId;
    draftId = (await persistDraftStep(3, values, draftId)) || draftId;
    if (!draftId) {
      throw new Error('Unable to save fund draft');
    }
    await dispatch(submitFundDraft(draftId));
    history.push('/funds-setup/fund-list');
  };

  const onSubmit = async (values: NewPlanType) => {
    try {
      const {
        schemeCode,
        schemeName,
        amcCode,
        bankName,
        bankBranch,
        ifscCode,
        bankAccountNumber,
        CVL_PWD,
        CVL_PASSCODE,
        CVL_USERNAME,
        CVL_APP_POS_CODE,
        CVL_ENABLE_NEW_API,
        CVL_JSON_AES_KEY,
        CVL_JSON_API_KEY,
        LEEGALITY_STAMP_SERIES = '',
        LEEGALITY_STAMP_SERIES_GROUP = '',
        LEEGALITY_STAMP_VALUE = null,
        LEEGALITY_USE_STAMP_SERIES = null,
        indMinNetworth = null,
        nonIndMinNetworth = null,
      } = values;

      const _CVL_ENABLE_NEW_API =
        CVL_ENABLE_NEW_API?.toString() === 'true'
          ? true
          : CVL_ENABLE_NEW_API?.toString() === 'false'
          ? false
          : null;
      const _CVL_JSON_AES_KEY = CVL_ENABLE_NEW_API?.toString() === 'true' ? CVL_JSON_AES_KEY : null;
      const _CVL_JSON_API_KEY = CVL_ENABLE_NEW_API?.toString() === 'true' ? CVL_JSON_API_KEY : null;

      if (!fundId) {
        await completeDraftFund(values);
        return;
      }

      await dispatch(
        updateFund(fundId, {
          schemeCode,
          schemeName,
          amcCode,
          bankName,
          bankBranch,
          ifscCode,
          bankAccountNumber,
          CVL_PWD,
          CVL_PASSCODE,
          CVL_USERNAME,
          CVL_APP_POS_CODE,
          CVL_ENABLE_NEW_API: _CVL_ENABLE_NEW_API,
          CVL_JSON_AES_KEY: _CVL_JSON_AES_KEY,
          CVL_JSON_API_KEY: _CVL_JSON_API_KEY,
          LEEGALITY_STAMP_SERIES,
          LEEGALITY_STAMP_SERIES_GROUP,
          LEEGALITY_STAMP_VALUE,
          LEEGALITY_USE_STAMP_SERIES:
            LEEGALITY_USE_STAMP_SERIES?.toString() === 'true' ? true : false,
          indMinNetworth,
          nonIndMinNetworth,
        })
      );
      history.push('fund-details', { fundId: fundId });
    } catch (e) {
      console.error((e as Error).message);
    }
  };

  const columns: any = [
    {
      header: 'Setup Fees %',
      label: 'performanceFee',
      renderEditDeleteCell: (row: updatedSetupFee): JSX.Element => {
        return (
          <>
            <MFTextField
              name={`setupFeeValues.${row.sno}.fee`}
              placeholder="Enter Setup Fee %"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
            />
          </>
        );
      },
    },
  ];
  const columns_equalisationPercentage: any = [
    {
      header: 'Equalisation %',
      label: 'equalisationPercent',
      renderEditDeleteCell: (row: updatedSetupFee): JSX.Element => {
        return (
          <>
            <MFTextField
              name={`equalisationPercentValues.${row.sno}.fee`}
              placeholder="Enter Equalisation %"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
            />
          </>
        );
      },
    },
  ];

  const AccessViewOrEditCell = (setupFeeValuesLength: number) => [
    {
      header: '',
      renderCell: (row: updatedSetupFee): JSX.Element => {
        return (
          <>
            {setupFeeValuesLength > 1 && (
              <Stack
                sx={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  display: 'inline-flex',
                  maxWidth: '100%',
                  py: 1.5,
                  '& .MuiFormControl-root': { width: '100%', m: 0 },
                }}>
                <Tooltip title="Delete">
                  <IconButton
                    sx={{
                      ':hover': {
                        bgcolor: '#F4FCFC',
                      },
                    }}
                    onClick={() => {
                      setSetupFeeToDelete({ index: row.sno });
                    }}>
                    <DeleteOutlineIcon color="error" fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </>
        );
      },
    },
  ];
  const AccessViewOrEditCell_equalisationPercentage = (_equalisationPercentageLength: number) => [
    {
      header: '',
      renderCell: (row: updatedSetupFee): JSX.Element => {
        return (
          <>
            {_equalisationPercentageLength > 1 && (
              <Stack
                sx={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  display: 'inline-flex',
                  maxWidth: '100%',
                  py: 1.5,
                  '& .MuiFormControl-root': { width: '100%', m: 0 },
                }}>
                <Tooltip title="Delete">
                  <IconButton
                    sx={{
                      ':hover': {
                        bgcolor: '#F4FCFC',
                      },
                    }}
                    onClick={() => {
                      setEqualisationPercentageToDelete({ index: row.sno });
                    }}>
                    <DeleteOutlineIcon color="error" fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </>
        );
      },
    },
  ];

  return (
    <>
      <Formik
        initialValues={editField}
        onSubmit={onSubmit}
        enableReinitialize={true}
        validationSchema={fundId ? editFundValidation : addFundValidation}>
        {({ handleSubmit, values, setValues, setErrors, setTouched, isSubmitting }) => {
          const sectionIndex = FUND_SECTIONS.findIndex((s) => s.key === activeSection);
          const currentStepSchema =
            sectionToStep(activeSection) === 1
              ? addFundStep1Validation
              : sectionToStep(activeSection) === 2
              ? addFundStep2Validation
              : addFundStep3Validation;
          const goBack = () => {
            if (!fundId && sectionIndex > 0) {
              setActiveSection(FUND_SECTIONS[sectionIndex - 1].key);
              return;
            }
            history.push(fundId ? '/funds-setup/fund-details' : '/funds-setup/fund-list', {
              fundId: fundId,
            });
          };
          const handleWizardNext = async () => {
            const step = sectionToStep(activeSection);
            const isValid = await validateYupSchemaOnValues(
              currentStepSchema,
              values,
              setErrors as (errors: Record<string, unknown>) => void,
              setTouched as (touched: Record<string, unknown>) => void
            );
            if (!isValid) return;
            try {
              await persistDraftStep(step, values, draftIdRef.current);
              if (sectionIndex < FUND_SECTIONS.length - 1) {
                setActiveSection(FUND_SECTIONS[sectionIndex + 1].key);
              }
            } catch (e) {
              console.error((e as Error).message);
            }
          };
          const handleSaveDraft = async () => {
            const step = sectionToStep(activeSection);
            const isValid = await validateYupSchemaOnValues(
              currentStepSchema,
              values,
              setErrors as (errors: Record<string, unknown>) => void,
              setTouched as (touched: Record<string, unknown>) => void
            );
            if (!isValid) return;
            try {
              await persistDraftStep(step, values, draftIdRef.current, true);
              history.push('/funds-setup/fund-list');
            } catch (e) {
              console.error((e as Error).message);
            }
          };
          return (
            <Box
              sx={{
                bgcolor: 'transparent',
                boxShadow: 'none',
                py: { xs: 2, sm: 3 },
                px: { xs: 2, sm: 4 },
                mt: 0,
              }}
              component="form"
              noValidate
              onSubmit={handleSubmit}>
              <Breadcrumbs
                separator=">"
                aria-label="breadcrumb"
                sx={{
                  mb: 1.5,
                  '& .MuiBreadcrumbs-separator': { mx: 0.75, color: '#6C737F' },
                  '.MuiTypography-root, .MuiLink-root': {
                    fontSize: 14,
                    color: '#111927',
                    fontFamily: 'Inter, sans-serif',
                  },
                }}>
                <Link component={RouterLink} to="/dashboard" underline="hover" color="inherit">
                  Home
                </Link>
                <Link
                  component={RouterLink}
                  to="/funds-setup/fund-list"
                  underline="hover"
                  color="inherit"
                  sx={{ color: '#111927 !important' }}>
                  Fund Set-Up
                </Link>
                <Typography sx={{ fontWeight: 500, color: '#6C737F !important' }}>
                  {fundId ? 'Edit Scheme Details' : savedDraftId ? 'Edit Draft Fund' : 'Add Funds'}
                </Typography>
              </Breadcrumbs>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                  mb: 3,
                }}>
                <Typography
                  component="h1"
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: { xs: 20, sm: 24 },
                    fontWeight: 700,
                    color: '#092056',
                  }}>
                  {fundId ? 'Edit Scheme Details' : savedDraftId ? 'Edit Draft Fund' : 'Add Funds'}
                </Typography>
              </Box>

              {!fundId && <FundFormStepper activeKey={activeSection} onSelect={setActiveSection} />}

              {loading ? (
                <LinearProgress sx={{ mt: 2 }} />
              ) : (
                <Box
                  sx={{
                    bgcolor: '#FFFFFF',
                    borderRadius: '16px',
                    border: '1px solid #F2F4F7',
                    boxShadow: '0px 4px 16px rgba(26, 43, 60, 0.04)',
                    p: { xs: 2, sm: 3, md: 4 },
                    minWidth: 0,
                    maxWidth: '100%',
                  }}>
                  <>
                    <Box sx={{ display: fundId || activeSection === 'basic' ? 'block' : 'none' }}>
                      <FundSectionHeading
                        title={FUND_SECTIONS[0].title}
                        subtitle={FUND_SECTIONS[0].subtitle}
                      />
                      <Grid container rowSpacing={1} columnSpacing={4} px={0}>
                        <Grid item xs={12}>
                          <RequiredFieldsMessage />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="schemeCode"
                            label="Scheme Code *"
                            placeholder="Enter Scheme Code"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="schemeName"
                            label="Scheme Name *"
                            placeholder="Enter Scheme Name"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="amcCode"
                            label="AMC Code"
                            placeholder="Enter AMC Code"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="bankName"
                            label="Bank Name"
                            placeholder="Enter Bank Name"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="bankBranch"
                            label="Bank Branch"
                            placeholder="Enter Bank Branch"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="ifscCode"
                            label="IFSC Code"
                            placeholder="Enter IFSC Code"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="bankAccountNumber"
                            label="Bank Account Number"
                            placeholder="Enter Bank Account Number"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="CVL_USERNAME"
                            label="CVL USERNAME *"
                            placeholder="Enter CVL USERNAME"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="CVL_PWD"
                            label="CVL PWD *"
                            placeholder="Enter CVL PWD"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="CVL_PASSCODE"
                            label="CVL PASSCODE *"
                            placeholder="Enter CVL PASSCODE"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="CVL_APP_POS_CODE"
                            label="CVL APP POS CODE *"
                            placeholder="Enter CVL APP POS CODE"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} mt={2}>
                          <UseRadioGroup
                            formLabel="CVL ENABLE NEW API *"
                            name={`CVL_ENABLE_NEW_API`}
                            items={Object.keys(YesNoBooleanMaster).map((ele) => ({
                              label: YesNoBooleanMaster[ele],
                              value: ele,
                            }))}
                            value={values.CVL_ENABLE_NEW_API?.toString()}
                          />
                        </Grid>
                        {values.CVL_ENABLE_NEW_API?.toString() === 'true' && (
                          <>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFTextField
                                name="CVL_JSON_AES_KEY"
                                label="CVL JSON AES KEY *"
                                placeholder="Enter CVL JSON AES KEY"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFTextField
                                name="CVL_JSON_API_KEY"
                                label="CVL JSON API KEY *"
                                placeholder="Enter CVL JSON API KEY"
                              />
                            </Grid>
                          </>
                        )}
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="LEEGALITY_STAMP_SERIES"
                            label="Stamp Paper Series *"
                            placeholder="Enter Stamp Paper Series"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="LEEGALITY_STAMP_SERIES_GROUP"
                            label="Stamp Paper Group *"
                            placeholder="Enter Stamp Paper Group"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="LEEGALITY_STAMP_VALUE"
                            label="Stamp Paper Value *"
                            placeholder="Enter Stamp Paper Value"
                            type="number"
                            onKeyDown={(e) => {
                              allowOnlyNumbers(e);
                            }}
                            trimOnBlur={false}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={4}
                          display={'flex'}
                          justifyContent={'center'}
                          alignItems={'center'}>
                          <UseRadioGroup
                            name={`LEEGALITY_USE_STAMP_SERIES`}
                            formLabel="Use Stamp Paper Series *"
                            items={Object.keys(IS_STAMP_PAPER_SERIES_MASTER).map((item) => ({
                              label: IS_STAMP_PAPER_SERIES_MASTER[item],
                              value: item,
                            }))}
                            value={values.LEEGALITY_USE_STAMP_SERIES?.toString()}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="indMinNetworth"
                            label="Individual Min Networth *"
                            placeholder="Enter individual min networth"
                          />
                          {values.indMinNetworth && !isNaN(Number(values.indMinNetworth)) && (
                            <Typography sx={{ fontSize: 13 }}>
                              {currencyConversion(values.indMinNetworth)}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="nonIndMinNetworth"
                            label="Non-Individual Min Networth *"
                            placeholder="Enter non-individual min networth"
                          />
                          {values.nonIndMinNetworth && !isNaN(Number(values.nonIndMinNetworth)) && (
                            <Typography sx={{ fontSize: 13 }}>
                              {currencyConversion(values.nonIndMinNetworth)}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                    {!fundId && (
                      <Box sx={{ display: activeSection === 'plan' ? 'block' : 'none' }}>
                        <FundSectionHeading
                          title={FUND_SECTIONS[1].title}
                          subtitle={FUND_SECTIONS[1].subtitle}
                        />
                        {!fundId ? (
                          <Grid container rowSpacing={1} columnSpacing={4} px={0}>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFTextField
                                name="planCode"
                                label="Plan Code *"
                                placeholder="Enter Plan Code"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFTextField
                                name="planDescription"
                                label="Plan Description *"
                                placeholder="Enter Plan Description"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFTextField
                                name="schemeNature"
                                label="Scheme Nature *"
                                placeholder="Enter Scheme Nature"
                              />
                            </Grid>
                            {/* <Grid item xs={12} sm={6} md={4}>
                      <MFTextField
                        name="registrationNumber"
                        label="Registration Number *"
                        placeholder="Enter Registration Number"
                      />
                    </Grid> */}
                            <Grid item xs={12} sm={6} md={4}>
                              <MFTextField
                                name="schemeCategory"
                                label="Scheme Category *"
                                placeholder="Enter Scheme Category"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MultipleSelect
                                name="fundmanagerIds"
                                label="Investment Manager Name *"
                                items={fundManager.map((item) => ({
                                  key: item.id,
                                  value: item.name,
                                }))}
                                renderText="Selected Investment Manager(s)"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFSelectField
                                name="requiredFundManagers"
                                label="No. of Investment Manager(s) to esign *"
                                items={getArrayLength(values?.fundmanagerIds?.length || 1)?.map(
                                  (thresholdValue) => ({
                                    key: thresholdValue,
                                    value: thresholdValue,
                                  })
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MultipleSelect
                                name="trusteeIds"
                                label="Trustee Name(s) *"
                                items={trustee.map((item) => ({
                                  key: item.id,
                                  value: item.name,
                                }))}
                                renderText="Selected Trustee(s)"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFSelectField
                                name="requiredTrustees"
                                label="No. of Trustee(s) to esign *"
                                items={getArrayLength(values?.trusteeIds?.length || 1)?.map(
                                  (thresholdValue) => ({
                                    key: thresholdValue,
                                    value: thresholdValue,
                                  })
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFTextField
                                name="country"
                                label="Country"
                                placeholder="Enter Country"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFSelectField
                                name="currency"
                                label="Currency"
                                items={Object.keys(CurrencyMaster).map((item) => ({
                                  key: CurrencyMaster[item].currency,
                                  value: item,
                                }))}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <DatePicker
                                label={'Scheme Start Date'}
                                inputLabelStyles={{
                                  transform: 'unset',
                                  fontSize: 14,
                                  fontWeight: 500,

                                  color: 'rgba(0,0,0,0.7)',
                                }}
                                placeholder={'Enter Scheme Start Date'}
                                name={`schemeStartDate`}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFTextField
                                name="lockInPeriod"
                                label="Lock In Period(In Year)"
                                placeholder="Enter Lock In Period(In Year)"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFTextField
                                name="lockInExtension"
                                label="Lock In Extension(In Year)"
                                placeholder="Enter Lock In Extension(In Year)"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFTextField
                                name="faceValue"
                                label="Face value"
                                placeholder="Enter Face value"
                                type="number"
                                onKeyDown={(e) => {
                                  allowOnlyNumbers(e);
                                }}
                                trimOnBlur={false}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFTextField
                                name="fundAddress1"
                                label="Fund Address 1"
                                placeholder="Enter Fund Address 1"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFTextField
                                name="fundAddress2"
                                label="Fund Address 2"
                                placeholder="Enter Fund Address 2"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFTextField
                                name="fundAddress3"
                                label="Fund Address 3"
                                placeholder="Enter Fund Address 3"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFTextField
                                name="custodian"
                                label="Custodian"
                                placeholder="Enter Custodian"
                              />
                            </Grid>
                            {/* <Grid item xs={12} sm={6} md={4}>
                            <MFTextField
                              name="setupFee"
                              label="Setup Fee"
                              placeholder="Enter Setup Fee"
                              type="number"
                              onKeyDown={(e) => {
                                allowOnlyNumbers(e);
                              }}
                              trimOnBlur={false}
                            />
                          </Grid> */}
                          </Grid>
                        ) : (
                          <Box sx={{ py: 4, textAlign: 'center' }}>
                            <Typography
                              sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: 14,
                                color: '#6B7280',
                                mb: 2,
                              }}>
                              Manage plans for this scheme from Plan Details.
                            </Typography>
                            <Button
                              variant="contained"
                              startIcon={<AddIcon />}
                              onClick={() => history.push('view-plans', { fundId })}
                              sx={{
                                textTransform: 'none',
                                bgcolor: '#2057A6',
                                boxShadow: 'none',
                                '&:hover': { bgcolor: '#1A4A8C', boxShadow: 'none' },
                              }}>
                              View Plans
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}
                    {!fundId && (
                      <Box sx={{ display: activeSection === 'investment' ? 'block' : 'none' }}>
                        <FundSectionHeading
                          title={FUND_SECTIONS[2].title}
                          subtitle={FUND_SECTIONS[2].subtitle}
                        />
                        {!fundId ? (
                          <Grid container rowSpacing={1} columnSpacing={4} px={0}>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFTextField
                                name="minCommitmentAmount"
                                label="Minimum Commitment Amount *"
                                placeholder="Enter Minimum Commitment Amount"
                                type="number"
                                onKeyDown={(e) => {
                                  allowOnlyNumbers(e);
                                }}
                                trimOnBlur={false}
                              />
                              {values.minCommitmentAmount != 0 && (
                                <Typography sx={{ fontSize: 13 }}>
                                  {currencyConversion(values.minCommitmentAmount, values.currency)}
                                </Typography>
                              )}
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFTextField
                                name="maxCommitmentAmount"
                                label="Maximum Commitment Amount *"
                                placeholder="Enter Maximum Commitment Amount"
                                type="number"
                                onKeyDown={(e) => {
                                  allowOnlyNumbers(e);
                                }}
                                trimOnBlur={false}
                              />
                              {values.maxCommitmentAmount != 0 && (
                                <Typography sx={{ fontSize: 13 }}>
                                  {currencyConversion(values.maxCommitmentAmount, values.currency)}
                                </Typography>
                              )}
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <UseRadioGroup
                                formLabel="Net Commitment Amount Applicable *"
                                name={`netCommitmentAmtApplicable`}
                                items={Object.keys(YesNoMaster).map((ele) => ({
                                  label: YesNoMaster[ele],
                                  value: ele,
                                }))}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFTextField
                                name="minContributionPercentage"
                                label="Minimum Contribution Percentage"
                                placeholder="Enter Minimum Contribution Percentage"
                                type="number"
                                onKeyDown={(e) => {
                                  allowOnlyNumbers(e);
                                }}
                                trimOnBlur={false}
                              />
                            </Grid>
                            {/* <Grid item xs={12} sm={6} md={4}>
                      <MFTextField
                        name="managementFee"
                        label="Management Fee"
                        placeholder="Enter Management Fee"
                        type="number"
                        onKeyDown={(e) => {
                          allowOnlyNumbers(e);
                        }}
                        trimOnBlur={false}
                      />
                    </Grid> */}
                            <Grid item xs={12} sm={6} md={4} mt={2}>
                              <UseRadioGroup
                                formLabel="Placement Fee Applicable *"
                                name={`placementFeeApplicable`}
                                items={Object.keys(YesNoMaster).map((ele) => ({
                                  label: YesNoMaster[ele],
                                  value: ele,
                                }))}
                              />
                            </Grid>
                            {isFieldApplicable(values.placementFeeApplicable || '') && (
                              <Grid item xs={12} sm={6} md={4}>
                                <MFTextField
                                  name="placementFee"
                                  label="Placement Fee %"
                                  placeholder="Enter Placement Fee %"
                                  type="number"
                                  onKeyDown={(e) => {
                                    allowOnlyNumbers(e);
                                  }}
                                  trimOnBlur={false}
                                />
                              </Grid>
                            )}
                            <Grid item xs={12} sm={6} md={4} mt={2}>
                              <UseRadioGroup
                                formLabel="Operating Expense Applicable *"
                                name={`operatingExpensesApplicable`}
                                items={Object.keys(YesNoMaster).map((ele) => ({
                                  label: YesNoMaster[ele],
                                  value: ele,
                                }))}
                              />
                            </Grid>
                            {isFieldApplicable(values.operatingExpensesApplicable || '') && (
                              <Grid item xs={12} sm={6} md={4}>
                                <MFTextField
                                  name="operatingExpenses"
                                  label="Operating Expenses %"
                                  placeholder="Enter Operating Expenses %"
                                  multiline
                                  rows={4.5}
                                />
                              </Grid>
                            )}
                            <Grid item xs={12} sm={6} md={4}>
                              <UseRadioGroup
                                formLabel="Equalisation Amount Applicable *"
                                name={`equalisationAmtApplicable`}
                                items={Object.keys(YesNoMaster).map((item) => ({
                                  label: YesNoMaster[item],
                                  value: item,
                                }))}
                              />
                            </Grid>
                            {isFieldApplicable(values.equalisationAmtApplicable) && (
                              <>
                                <Grid item xs={12} sm={6} md={4}>
                                  <UseRadioGroup
                                    formLabel="Equalisation Amount Type *"
                                    name={`equalisationAmtType`}
                                    items={Object.keys(FEE_TYPE_MASTER).map((item) => ({
                                      label: FEE_TYPE_MASTER[item],
                                      value: item,
                                    }))}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                  <MFTextField
                                    name="defaultEqualisationAmt"
                                    label="Equalisation Amount *"
                                    placeholder="Enter Equalisation Amount"
                                    type="number"
                                    onKeyDown={(e) => {
                                      allowOnlyNumbers(e);
                                    }}
                                    trimOnBlur={false}
                                  />
                                  {values.defaultEqualisationAmt != 0 && (
                                    <Typography sx={{ fontSize: 13 }}>
                                      {currencyConversion(
                                        values.defaultEqualisationAmt,
                                        values.currency
                                      )}
                                    </Typography>
                                  )}
                                </Grid>
                              </>
                            )}
                            {isFieldVariable(
                              values.equalisationAmtType,
                              values.equalisationAmtApplicable
                            ) && (
                              <>
                                <Grid item xs={12} sm={6} md={4}>
                                  <MFTextField
                                    name="minEqualisationAmt"
                                    label="Minimum Equalisation Amount*"
                                    placeholder="Enter Minimum Equalisation Amount"
                                    type="number"
                                    onKeyDown={(e) => {
                                      allowOnlyNumbers(e);
                                    }}
                                    trimOnBlur={false}
                                  />
                                  {values.minEqualisationAmt != 0 && (
                                    <Typography sx={{ fontSize: 13 }}>
                                      {currencyConversion(
                                        values.minEqualisationAmt,
                                        values.currency
                                      )}
                                    </Typography>
                                  )}
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                  <MFTextField
                                    name="maxEqualisationAmt"
                                    label="Maximum Equalisation Amount *"
                                    placeholder="Enter Equalisation Amount"
                                    type="number"
                                    onKeyDown={(e) => {
                                      allowOnlyNumbers(e);
                                    }}
                                    trimOnBlur={false}
                                  />
                                  {values.maxEqualisationAmt != 0 && (
                                    <Typography sx={{ fontSize: 13 }}>
                                      {currencyConversion(
                                        values.maxEqualisationAmt,
                                        values.currency
                                      )}
                                    </Typography>
                                  )}
                                </Grid>
                              </>
                            )}
                            <Grid item xs={12} sm={6} md={4}>
                              <MFTextField
                                name="tcLink"
                                label={`T&C Link`}
                                placeholder={`Enter T&C Link`}
                              />
                            </Grid>
                            {/* empty grid for proper alignment */}
                            <Grid xs={12}></Grid>
                            <Grid item xs={12} sm={6} sx={{ mt: 2 }}>
                              <Box sx={{ display: 'flex' }}>
                                <MFCheckbox name={`isApplicableForDirect`} label="Direct" />
                                <MFCheckbox
                                  name={`isApplicableForDistributor`}
                                  label="Distributor"
                                  onChange={({ target: { checked } }) => {
                                    setValues({
                                      ...values,
                                      isApplicableForDistributor: checked,
                                      distributorIds: [],
                                    });
                                  }}
                                  checked={values.isApplicableForDistributor}
                                />
                              </Box>
                              {values.isApplicableForDistributor && (
                                <MultipleSelect
                                  name="distributorIds"
                                  label="Applicable Distributor(s) *"
                                  items={distributorList.map((item) => ({
                                    key: item.id,
                                    value: `${item.arnCode || 'N/A'} - ${item.name} (${item.type})`,
                                  }))}
                                  renderText="Selected Distributor(s)"
                                />
                              )}
                            </Grid>
                            <Grid item xs={12}>
                              <UseRadioGroup
                                formLabel="Setup Fee Applicable *"
                                name={`setupFeeApplicable`}
                                items={Object.keys(YesNoMaster).map((ele) => ({
                                  label: YesNoMaster[ele],
                                  value: ele,
                                }))}
                              />
                            </Grid>
                            {isApplicableSetupfee(values.setupFeeApplicable) && (
                              <>
                                <Grid item xs={12} sm={12} md={12}>
                                  <DataTable
                                    tableData={(values.setupFeeValues as addSetupFee[]).filter(
                                      (ele) => ele.isActive
                                    )}
                                    tableHeader={[
                                      ...columns,
                                      ...AccessViewOrEditCell(
                                        (values.setupFeeValues as addSetupFee[]).filter(
                                          (e) => e.isActive
                                        ).length
                                      ),
                                    ]}
                                    noOfPages={1}
                                    renderAdditionalRow={true}
                                    tableBodyCustomStyles={{
                                      '.MuiTableCell-root': {
                                        borderBottom: 'none',
                                      },
                                    }}
                                    tableHeaderCustomStyles={{
                                      '.MuiTableCell-head': {
                                        fontFamily: 'Poppins, sans-serif',
                                        fontSize: 14,
                                        fontWeight: 500,
                                        color: 'primary.main',
                                        textAlign: 'left',
                                        pl: 2,
                                      },
                                    }}
                                    rowCustomStyles={{
                                      '.MuiTableCell-root': {
                                        py: 0,
                                        overflowWrap: 'anywhere',
                                        textAlign: 'left',
                                        verticalAlign: 'top',
                                      },
                                    }}
                                    boxStyles={{
                                      minHeight: '0px',
                                      minWidth: 450,
                                      maxHeight: 'unset',
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    sx={{
                                      color: 'primary.main',
                                      fontWeight: 500,
                                      fontSize: 14,
                                      mt: 2,
                                    }}
                                    onClick={() =>
                                      setValues({
                                        ...values,
                                        setupFeeValues: [
                                          ...values.setupFeeValues,
                                          { ...newSetupFee, sno: values.setupFeeValues.length },
                                        ] as addSetupFee[],
                                      })
                                    }>
                                    Add Setup Fee
                                  </Button>
                                </Grid>
                              </>
                            )}
                            <ConfirmationDialog
                              message={'Are you sure you want to delete setup fee ?'}
                              open={setupFeeToDelete !== null}
                              setOpen={() => {
                                setSetupFeeToDelete(null);
                              }}
                              onSave={() => {
                                const remainedSetupFee = (
                                  values.setupFeeValues as updatedSetupFee[]
                                ).map((obj) => {
                                  if (obj.sno === setupFeeToDelete?.index) {
                                    return {
                                      ...obj,
                                      isActive: false,
                                    };
                                  }
                                  return obj;
                                });
                                setValues({
                                  ...values,
                                  setupFeeValues: remainedSetupFee,
                                });
                                setSetupFeeToDelete(null);
                              }}
                              onCancel={() => {
                                setSetupFeeToDelete(null);
                              }}
                            />
                            <Grid item xs={12}>
                              <UseRadioGroup
                                formLabel="Equalisation % Applicable *"
                                name={`equalisationPercentApplicable`}
                                items={Object.keys(YesNoMaster).map((ele) => ({
                                  label: YesNoMaster[ele],
                                  value: ele,
                                }))}
                              />
                            </Grid>
                            {isApplicableSetupfee(values.equalisationPercentApplicable) && (
                              <>
                                <Grid item xs={12} sm={12} md={12}>
                                  <DataTable
                                    tableData={(
                                      values.equalisationPercentValues as addSetupFee[]
                                    ).filter((ele) => ele.isActive)}
                                    tableHeader={[
                                      ...columns_equalisationPercentage,
                                      ...AccessViewOrEditCell_equalisationPercentage(
                                        (values.equalisationPercentValues as addSetupFee[]).filter(
                                          (e) => e.isActive
                                        ).length
                                      ),
                                    ]}
                                    noOfPages={1}
                                    renderAdditionalRow={true}
                                    tableBodyCustomStyles={{
                                      '.MuiTableCell-root': {
                                        borderBottom: 'none',
                                      },
                                    }}
                                    tableHeaderCustomStyles={{
                                      '.MuiTableCell-head': {
                                        fontFamily: 'Poppins, sans-serif',
                                        fontSize: 14,
                                        fontWeight: 500,
                                        color: 'primary.main',
                                        textAlign: 'left',
                                        pl: 2,
                                      },
                                    }}
                                    rowCustomStyles={{
                                      '.MuiTableCell-root': {
                                        py: 0,
                                        overflowWrap: 'anywhere',
                                        textAlign: 'left',
                                        verticalAlign: 'top',
                                      },
                                    }}
                                    boxStyles={{
                                      minHeight: '0px',
                                      minWidth: 450,
                                      maxHeight: 'unset',
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    sx={{
                                      color: 'primary.main',
                                      fontWeight: 500,
                                      fontSize: 14,
                                      mt: 2,
                                    }}
                                    onClick={() =>
                                      setValues({
                                        ...values,
                                        equalisationPercentValues: [
                                          ...values.equalisationPercentValues,
                                          {
                                            ...newSetupFee,
                                            sno: values.equalisationPercentValues.length,
                                          },
                                        ] as addSetupFee[],
                                      })
                                    }>
                                    Add Equalisation %
                                  </Button>
                                </Grid>
                              </>
                            )}
                            <ConfirmationDialog
                              message={'Are you sure you want to delete Equalisation % ?'}
                              open={_equalisationPercentageToDelete !== null}
                              setOpen={() => {
                                setEqualisationPercentageToDelete(null);
                              }}
                              onSave={() => {
                                const remainedSetupFee = (
                                  values.equalisationPercentValues as updatedSetupFee[]
                                ).map((obj) => {
                                  if (obj.sno === _equalisationPercentageToDelete?.index) {
                                    return {
                                      ...obj,
                                      isActive: false,
                                    };
                                  }
                                  return obj;
                                });
                                setValues({
                                  ...values,
                                  equalisationPercentValues: remainedSetupFee,
                                });
                                setEqualisationPercentageToDelete(null);
                              }}
                              onCancel={() => {
                                setEqualisationPercentageToDelete(null);
                              }}
                            />
                          </Grid>
                        ) : (
                          <Box sx={{ py: 4, textAlign: 'center' }}>
                            <Typography
                              sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: 14,
                                color: '#6B7280',
                              }}>
                              Investment and commitment settings are managed at plan level.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        mt: 4,
                        pt: 2,
                      }}>
                      {access && (
                        <Box sx={{ '& .MuiLoadingButton-root': { mt: 0, mb: 0, minWidth: 140 } }}>
                          {fundId ? (
                            <MFSubmitButton label="Save" variant="contained" fullWidth={false} />
                          ) : (
                            <Button
                              type="button"
                              variant="outlined"
                              disabled={isSubmitting}
                              onClick={handleSaveDraft}
                              sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: 14,
                                fontWeight: 500,
                                textTransform: 'none',
                                minWidth: 140,
                                height: 40,
                                px: 3,
                                color: '#111927',
                                bgcolor: '#FFFFFF',
                                borderColor: '#E5E7EB',
                                borderRadius: '8px',
                                boxShadow: 'none',
                                '&:hover': {
                                  bgcolor: '#F9FAFB',
                                  borderColor: '#D1D5DB',
                                  boxShadow: 'none',
                                },
                              }}>
                              Save As Draft
                            </Button>
                          )}
                        </Box>
                      )}
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={goBack}
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: 14,
                          fontWeight: 500,
                          textTransform: 'none',
                          color: '#111927',
                          borderColor: '#E5E7EB',
                          borderRadius: '8px',
                          px: 3,
                          height: 40,
                        }}>
                        Back
                      </Button>
                      {!fundId &&
                        (sectionIndex < FUND_SECTIONS.length - 1 ? (
                          <Button
                            type="button"
                            variant="contained"
                            disabled={isSubmitting}
                            onClick={handleWizardNext}
                            sx={{
                              fontFamily: 'Inter, sans-serif',
                              fontSize: 14,
                              fontWeight: 500,
                              textTransform: 'none',
                              bgcolor: '#2057A6',
                              color: '#FFFFFF',
                              borderRadius: '8px',
                              px: 3.5,
                              height: 40,
                              boxShadow: 'none',
                              '&:hover': { bgcolor: '#1A4A8C', boxShadow: 'none' },
                            }}>
                            Next
                          </Button>
                        ) : (
                          access && (
                            <Box
                              sx={{ '& .MuiLoadingButton-root': { mt: 0, mb: 0, minWidth: 88 } }}>
                              <MFSubmitButton label="Submit" fullWidth={false} />
                            </Box>
                          )
                        ))}
                    </Box>
                  </>
                </Box>
              )}
            </Box>
          );
        }}
      </Formik>
    </>
  );
}
